/**
 * Browser-side observability: `getStats()` deltas, local VAD, and turn marks.
 *
 * WHY THIS FILE IS PART OF THE PRODUCT AND NOT LOGGING. Over a TCP WebSocket
 * relay the numbers below cannot exist: loss appears as latency and retransmits
 * appear as jitter, indistinguishably. Because our media plane is real WebRTC we
 * get RFC 3550 loss, jitter, RTT and jitter-buffer concealment counters from the
 * one vantage point that can see the last mile — the listener's own machine.
 * That is the single largest instrumentation upgrade in the project, and it is
 * only available here.
 *
 * THREE THINGS THAT ARE EASY TO GET WRONG AND ARE DONE DELIBERATELY:
 *
 * 1. DELTAS, NOT TOTALS. `getStats()` counters are cumulative for the lifetime
 *    of the connection. Shipping totals every two seconds would make the server
 *    difference them, and the server would have to know which samples it had
 *    already seen to do that correctly. We difference here, where the previous
 *    sample provably belongs to the same peer connection.
 *
 * 2. `avgJitterBufferMs` IS A RATIO OVER THE WINDOW, not `jitterBufferDelay`
 *    divided by the total emitted count. Dividing the cumulative pair gives the
 *    average over the whole call, which is smooth, reassuring and useless for
 *    finding the fifteen seconds where the network fell over. Both members of
 *    the pair are differenced and then divided.
 *
 * 3. THE ASSISTANT PLAYOUT MARK IS PACKET ARRIVAL, NOT AUDIBLE PLAYOUT. On the
 *    Encoded Transform path the mark is taken when the first packet of a burst
 *    reaches the receiver transform, which is BEFORE the jitter buffer. True
 *    audibility is that instant plus the jitter buffer delay, which is exactly
 *    what `avgJitterBufferMs` carries — so the server can add them and the two
 *    numbers stay separately attributable. Collapsing them here would destroy
 *    the distinction between "the network held it back" and "the model was slow".
 */
import type { RtcStatsSample, TurnMark } from '@voiceai/wire'
import { nowEpochMs } from './control'

/** How often we sample getStats. Two seconds is the design's figure and it is
 *  also about the point where a Chromium stats collection stops being free. */
const STATS_INTERVAL_MS = 2000
/** VAD polling. 50 ms is four Opus packets — fine enough to time a turn, coarse
 *  enough to be invisible on a CPU graph. */
const VAD_POLL_MS = 50
/** Speech must stay quiet this long before the turn is called ended. Shorter and
 *  every inter-word pause becomes a turn boundary. */
const VAD_HANGOVER_MS = 600
/** Absolute noise gate. Below this RMS nothing is speech no matter what the
 *  adaptive floor says, which stops a dead-silent room from self-triggering. */
const VAD_ABSOLUTE_FLOOR = 0.006
/** How far above the tracked noise floor counts as speech. */
const VAD_MARGIN = 3.2

function num(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined
}

/** Delta of two cumulative counters, or undefined if we cannot trust it. A
 *  negative delta means the stat was reset (ICE restart) — report nothing rather
 *  than a negative packet count. */
function delta(now: number | undefined, before: number | undefined): number | undefined {
  if (now === undefined) return undefined
  if (before === undefined) return undefined
  const d = now - before
  return d >= 0 ? d : undefined
}

interface RawStats {
  atMs: number
  packetsReceived?: number
  packetsLost?: number
  jitterMs?: number
  jitterBufferDelay?: number
  jitterBufferEmittedCount?: number
  concealedSamples?: number
  concealmentEvents?: number
  roundTripTimeMs?: number
  audioLevelIn?: number
  audioLevelOut?: number
}

/**
 * Pull the audio-relevant rows out of a stats report.
 *
 * The rows are read as loose records rather than through the typed `RTCStats`
 * subclasses: the typed shapes in the DOM lib lag the implementations by a year
 * or more, and half of the counters we want (`concealedSamples`,
 * `jitterBufferEmittedCount`) are missing or optional in a way that makes every
 * access a cast anyway.
 */
function readStats(report: RTCStatsReport): RawStats {
  const out: RawStats = { atMs: nowEpochMs() }
  let selectedPairId: string | null = null
  const pairs = new Map<string, Record<string, unknown>>()

  report.forEach((raw) => {
    const s = raw as unknown as Record<string, unknown>
    const type = s['type']
    const kind = s['kind'] ?? s['mediaType']
    if (type === 'inbound-rtp' && kind === 'audio') {
      out.packetsReceived = num(s['packetsReceived'])
      out.packetsLost = num(s['packetsLost'])
      const jitter = num(s['jitter'])
      out.jitterMs = jitter === undefined ? undefined : jitter * 1000
      out.jitterBufferDelay = num(s['jitterBufferDelay'])
      out.jitterBufferEmittedCount = num(s['jitterBufferEmittedCount'])
      out.concealedSamples = num(s['concealedSamples'])
      out.concealmentEvents = num(s['concealmentEvents'])
      out.audioLevelIn = num(s['audioLevel'])
    } else if (type === 'remote-inbound-rtp' && kind === 'audio') {
      // This is the provider telling us what IT measured for the round trip,
      // which is the only RTT that reflects the actual media path rather than
      // the STUN consent checks.
      const rtt = num(s['roundTripTime'])
      if (rtt !== undefined) out.roundTripTimeMs = rtt * 1000
    } else if (type === 'media-source' && kind === 'audio') {
      out.audioLevelOut = num(s['audioLevel'])
    } else if (type === 'candidate-pair') {
      pairs.set(String(s['id']), s)
      if (s['selected'] === true || s['nominated'] === true) selectedPairId = String(s['id'])
    } else if (type === 'transport') {
      const sel = s['selectedCandidatePairId']
      if (typeof sel === 'string') selectedPairId = sel
    }
  })

  if (out.roundTripTimeMs === undefined && selectedPairId !== null) {
    const pair = pairs.get(selectedPairId)
    const rtt = pair ? num(pair['currentRoundTripTime']) : undefined
    if (rtt !== undefined) out.roundTripTimeMs = rtt * 1000
  }
  return out
}

export interface TelemetryHooks {
  onSample(sample: RtcStatsSample): void
  onSpeech(state: 'start' | 'stop'): void
  onLog(level: 'info' | 'warn' | 'error', message: string): void
}

export class Telemetry {
  private statsTimer: ReturnType<typeof setInterval> | null = null
  private vadTimer: ReturnType<typeof setInterval> | null = null
  private prev: RawStats | null = null

  private analyser: AnalyserNode | null = null
  private vadNodes: AudioNode[] = []
  private vadBuf: Float32Array<ArrayBuffer> = new Float32Array(1024)
  private noiseFloor = 0.004
  private speaking = false
  private lastLoudMs = 0

  /** Turns that are finished and not yet shipped. */
  private pendingTurns: TurnMark[] = []
  private current: TurnMark | null = null
  private turnSeq = 0
  private assistantSpeaking = false

  /** The most recent delta sample, kept for the diagnostics panel. Read-only
   *  from the outside and NOT cleared by `drain()` — the panel would flicker to
   *  dashes every time telemetry was shipped. */
  latest: RtcStatsSample | null = null
  /** Samples not yet handed to the control channel. */
  private unsent: RtcStatsSample[] = []
  /** Every turn we have observed, for the UI. Bounded by the 60-minute cap. */
  readonly turns: TurnMark[] = []

  constructor(
    private readonly pc: RTCPeerConnection,
    private readonly hooks: TelemetryHooks,
  ) {}

  start(): void {
    this.statsTimer = setInterval(() => void this.sample(), STATS_INTERVAL_MS)
  }

  /**
   * Attach the local voice-activity tap.
   *
   * THE GAIN(0) TERMINATION IS NOT DECORATIVE. A Web Audio graph that does not
   * reach the destination is not guaranteed to be pulled, so an AnalyserNode
   * hanging off a MediaStreamSource with nothing downstream can read zeroes
   * forever — which presents as "VAD never fires" and is indistinguishable from
   * a broken microphone. Routing through a zero gain to the destination keeps the
   * graph live and emits nothing audible.
   *
   * The AudioContext must already be RESUMED, inside the user gesture that
   * started the call. A suspended context here is the classic silent failure.
   */
  attachVad(ctx: AudioContext, mic: MediaStream): void {
    try {
      const src = ctx.createMediaStreamSource(mic)
      const analyser = ctx.createAnalyser()
      analyser.fftSize = 2048
      analyser.smoothingTimeConstant = 0.2
      const mute = ctx.createGain()
      mute.gain.value = 0
      src.connect(analyser)
      analyser.connect(mute)
      mute.connect(ctx.destination)
      this.analyser = analyser
      this.vadBuf = new Float32Array(analyser.fftSize)
      this.vadNodes = [src, analyser, mute]
      this.vadTimer = setInterval(() => this.pollVad(), VAD_POLL_MS)
    } catch (e) {
      this.hooks.onLog('warn', `local VAD unavailable: ${String(e)}`)
    }
  }

  private pollVad(): void {
    const analyser = this.analyser
    if (!analyser) return
    analyser.getFloatTimeDomainData(this.vadBuf)
    let sum = 0
    for (let i = 0; i < this.vadBuf.length; i++) {
      const v = this.vadBuf[i] ?? 0
      sum += v * v
    }
    const rms = Math.sqrt(sum / this.vadBuf.length)
    const now = nowEpochMs()

    // The floor tracks downward fast and upward slowly, so a fan that starts up
    // gets absorbed within a few seconds while a sentence never raises it.
    if (rms < this.noiseFloor) this.noiseFloor += (rms - this.noiseFloor) * 0.25
    else this.noiseFloor += (rms - this.noiseFloor) * 0.002

    const loud = rms > VAD_ABSOLUTE_FLOOR && rms > this.noiseFloor * VAD_MARGIN
    if (loud) this.lastLoudMs = now

    if (loud && !this.speaking) {
      this.speaking = true
      this.onUserSpeechStart(now)
      this.hooks.onSpeech('start')
    } else if (!loud && this.speaking && now - this.lastLoudMs > VAD_HANGOVER_MS) {
      this.speaking = false
      this.onUserSpeechEnd(this.lastLoudMs)
      this.hooks.onSpeech('stop')
    }
  }

  private async sample(): Promise<void> {
    let report: RTCStatsReport
    try {
      report = await this.pc.getStats()
    } catch {
      return
    }
    const raw = readStats(report)
    const prev = this.prev
    this.prev = raw
    if (!prev) return

    const windowMs = Math.round(raw.atMs - prev.atMs)
    if (windowMs <= 0) return

    const jbDelay = delta(raw.jitterBufferDelay, prev.jitterBufferDelay)
    const jbCount = delta(raw.jitterBufferEmittedCount, prev.jitterBufferEmittedCount)

    const sample: RtcStatsSample = {
      atMs: Math.round(raw.atMs),
      windowMs,
      packetsReceived: delta(raw.packetsReceived, prev.packetsReceived),
      packetsLost: delta(raw.packetsLost, prev.packetsLost),
      jitterMs: raw.jitterMs === undefined ? undefined : round2(raw.jitterMs),
      // jitterBufferDelay is SECONDS accumulated over emitted samples, so the
      // ratio is seconds-per-sample; times 1000 gives the milliseconds a sample
      // waited. Guard the denominator: a window with no audio emits nothing.
      avgJitterBufferMs:
        jbDelay !== undefined && jbCount !== undefined && jbCount > 0
          ? round2((jbDelay / jbCount) * 1000)
          : undefined,
      concealedSamples: delta(raw.concealedSamples, prev.concealedSamples),
      concealmentEvents: delta(raw.concealmentEvents, prev.concealmentEvents),
      roundTripTimeMs: raw.roundTripTimeMs === undefined ? undefined : round2(raw.roundTripTimeMs),
      audioLevelOut: raw.audioLevelOut === undefined ? undefined : round3(raw.audioLevelOut),
      audioLevelIn: raw.audioLevelIn === undefined ? undefined : round3(raw.audioLevelIn),
    }
    this.latest = sample
    this.unsent.push(sample)
    // A control socket that has been down for a while must not accumulate an
    // unbounded backlog of stats nobody will ever look at. Thirty samples is a
    // minute of history, which is as far back as a diagnosis reaches anyway.
    if (this.unsent.length > 30) this.unsent.splice(0, this.unsent.length - 30)
    this.hooks.onSample(sample)
  }

  // --------------------------------------------------------------------------
  // Turn marks
  // --------------------------------------------------------------------------

  /** The DO's projection of `output_audio_buffer.started/.stopped`, which is the
   *  provider telling us when it is actually pushing RTP. Used only to decide
   *  whether a user speech onset was a barge-in. */
  setAssistantSpeaking(speaking: boolean): void {
    this.assistantSpeaking = speaking
  }

  private openTurn(): TurnMark {
    const turn: TurnMark = { n: this.turnSeq++ }
    this.current = turn
    this.turns.push(turn)
    return turn
  }

  private closeTurn(): void {
    if (this.current) this.pendingTurns.push(this.current)
    this.current = null
  }

  private onUserSpeechStart(atMs: number): void {
    if (this.current && this.assistantSpeaking) {
      // Interrupting audio that is still being pushed. The barge-in belongs to
      // the turn being cut off, not to the new one.
      this.current.bargedIn = true
    }
    if (this.current && (this.current.userSpeechStartMs !== undefined || this.current.assistantFirstPlayoutMs !== undefined)) {
      this.closeTurn()
    }
    const turn = this.current ?? this.openTurn()
    turn.userSpeechStartMs = Math.round(atMs)
  }

  private onUserSpeechEnd(atMs: number): void {
    if (this.current && this.current.userSpeechStartMs !== undefined) {
      this.current.userSpeechEndMs = Math.round(atMs)
    }
  }

  /**
   * The first assistant packet of a burst.
   *
   * A burst with no preceding user speech is a real and common turn — the
   * greeting is exactly that — so this opens a turn rather than discarding the
   * mark.
   */
  noteAssistantBurst(rtp: string, epochMs: number): void {
    if (this.current && this.current.assistantFirstPlayoutMs !== undefined) this.closeTurn()
    const turn = this.current ?? this.openTurn()
    turn.assistantFirstPlayoutMs = Math.round(epochMs)
    turn.assistantFirstRtp = rtp
  }

  /**
   * Hand over everything worth sending. Turns that are still open are held back
   * — a half-measured turn on the server is worse than a late one — except at
   * the very end of the call, where `final` flushes the open turn too.
   */
  drain(final: boolean): { turns?: TurnMark[]; stats?: RtcStatsSample[] } {
    if (final) this.closeTurn()
    const turns = this.pendingTurns.length > 0 ? this.pendingTurns.splice(0) : undefined
    const stats = this.unsent.length > 0 ? this.unsent.splice(0) : undefined
    const out: { turns?: TurnMark[]; stats?: RtcStatsSample[] } = {}
    if (turns) out.turns = turns
    if (stats) out.stats = stats
    return out
  }

  stop(): void {
    if (this.statsTimer !== null) clearInterval(this.statsTimer)
    if (this.vadTimer !== null) clearInterval(this.vadTimer)
    this.statsTimer = null
    this.vadTimer = null
    for (const n of this.vadNodes) {
      try {
        n.disconnect()
      } catch {
        /* a node from a closed context throws; nothing to do */
      }
    }
    this.vadNodes = []
    this.analyser = null
  }
}

function round2(n: number): number {
  return Math.round(n * 100) / 100
}
function round3(n: number): number {
  return Math.round(n * 1000) / 1000
}
